import React, { createContext, useContext, useState, useEffect } from 'react';
import { fetchCart, addToCart as apiAddToCart, removeFromCart as apiRemoveFromCart, checkoutCart as apiCheckoutCart } from '../api/client';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

const CartContext = createContext();

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [itemCount, setItemCount] = useState(0);
  const [subtotalCents, setSubtotalCents] = useState(0);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadCart = async () => {
    if (!user) {
      // Local storage fallback for guests
      try {
        const saved = localStorage.getItem('local_cart');
        if (saved) {
          const parsed = JSON.parse(saved);
          setItems(parsed);
          setItemCount(parsed.length);
          const sub = parsed.reduce((acc, item) => acc + (item.price_cents || 0), 0);
          setSubtotalCents(sub);
        } else {
          setItems([]);
          setItemCount(0);
          setSubtotalCents(0);
        }
      } catch (e) {
        // ignore
      }
      return;
    }

    try {
      const res = await fetchCart();
      if (res?.data) {
        setItems(res.data.items || []);
        setItemCount(res.data.itemCount || 0);
        setSubtotalCents(res.data.subtotalCents || 0);
      }
    } catch (err) {
      console.error('Failed to load cart:', err);
    }
  };

  useEffect(() => {
    loadCart();
  }, [user]);

  const addItem = async (template) => {
    if (!template?.id) return;

    if (!user) {
      // Guest cart
      const exists = items.some(i => i.template_id === template.id || i.id === template.id);
      if (exists) {
        toast.error('Item is already in your cart');
        setIsCartOpen(true);
        return;
      }
      const newItem = {
        template_id: template.id,
        title: template.title,
        price_cents: template.price_cents || 0,
        is_free: template.is_free,
        preview_url: template.image || template.preview_url,
        designer_name: template.designer || template.designer_name || 'Creator',
      };
      const updated = [...items, newItem];
      setItems(updated);
      setItemCount(updated.length);
      const sub = updated.reduce((acc, item) => acc + (item.price_cents || 0), 0);
      setSubtotalCents(sub);
      localStorage.setItem('local_cart', JSON.stringify(updated));
      toast.success('Added to cart!');
      setIsCartOpen(true);
      return;
    }

    try {
      setLoading(true);
      const res = await apiAddToCart(template.id);
      if (res?.isAlreadyInCart) {
        toast.error('Item is already in your cart');
      } else {
        toast.success('Added to cart!');
        await loadCart();
      }
      setIsCartOpen(true);
    } catch (err) {
      toast.error(err.message || 'Failed to add item');
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (templateId) => {
    if (!user) {
      const updated = items.filter(i => (i.template_id !== templateId && i.id !== templateId));
      setItems(updated);
      setItemCount(updated.length);
      const sub = updated.reduce((acc, item) => acc + (item.price_cents || 0), 0);
      setSubtotalCents(sub);
      localStorage.setItem('local_cart', JSON.stringify(updated));
      toast.success('Item removed');
      return;
    }

    try {
      await apiRemoveFromCart(templateId);
      toast.success('Item removed');
      await loadCart();
    } catch (err) {
      toast.error('Failed to remove item');
    }
  };

  const [currency, setCurrency] = useState('GHS'); // 'GHS' | 'USD'

  const checkout = async (paymentMethod = 'momo') => {
    if (!user) {
      toast.error('Please log in to complete your checkout');
      return false;
    }

    try {
      setLoading(true);
      // 1. Initialize Paystack Transaction
      const initRes = await fetch('/api/payments/initialize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user ? { 'Authorization': `Bearer ${user.access_token || ''}` } : {})
        },
        body: JSON.stringify({
          currency,
          payment_method: paymentMethod,
          callback_url: `${window.location.origin}/library?payment=success`
        })
      }).then(r => r.json());

      if (!initRes.success) {
        throw new Error(initRes.error || 'Failed to initialize payment');
      }

      // If all items are free, checkout is already complete
      if (initRes.is_free) {
        toast.success('Free templates unlocked in your Library! 🎉');
        setItems([]);
        setItemCount(0);
        setSubtotalCents(0);
        return true;
      }

      // If live Paystack authorization_url returned, redirect to Paystack secure checkout
      if (initRes.authorization_url) {
        window.location.href = initRes.authorization_url;
        return true;
      }

      // Sandbox verification for test mode: verify reference and unlock
      const verifyRes = await fetch(`/api/payments/verify/${initRes.reference}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user ? { 'Authorization': `Bearer ${user.access_token || ''}` } : {})
        }
      }).then(r => r.json());

      if (verifyRes.success) {
        toast.success('Paystack payment verified! Templates added to Library. 🎉');
        setItems([]);
        setItemCount(0);
        setSubtotalCents(0);
        return true;
      } else {
        throw new Error(verifyRes.error || 'Payment verification failed');
      }
    } catch (err) {
      toast.error(err.message || 'Checkout failed');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider value={{
      items,
      itemCount,
      subtotalCents,
      formattedSubtotal: currency === 'GHS' 
        ? `GH₵ ${(Math.round(((Number(subtotalCents) || 0) / 100) * 15 * 100) / 100).toFixed(2)}`
        : `$${((Number(subtotalCents) || 0) / 100).toFixed(2)}`,
      currency,
      setCurrency,
      isCartOpen,
      setIsCartOpen,
      addItem,
      removeItem,
      checkout,
      refreshCart: loadCart,
      loading,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
